import React, { useEffect, useState } from "react";
import { getDatabase, ref, onValue } from "firebase/database";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import "./stockStatus.css";
import { FaInfoCircle, FaChevronDown, FaBatteryFull, FaBatteryHalf, FaBatteryEmpty } from "react-icons/fa";

const AdminStockStatus = () => {
  const [users, setUsers] = useState({});
  const [codes, setCodes] = useState({});
  const [loading, setLoading] = useState(true);
  const [adminId, setAdminId] = useState(null);
  const [selected, setSelected] = useState({ userId: "" });
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  // Get logged-in admin ID
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setAdminId(user.uid);
      } else {
        console.warn("No admin is logged in.");
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch all users who have updated stock + company names and phone in real-time
  useEffect(() => {
    if (!adminId) return;

    const db = getDatabase();
    const codesRef = ref(db, "new-code");

    const unsubscribe = onValue(codesRef, (snapshot) => {
      if (snapshot.exists()) {
        const usersData = snapshot.val();
        const processedUsers = {};
        const fetchPromises = [];

        for (const userId of Object.keys(usersData)) {
          const userCodes = usersData[userId]?.codes || {};
          const filteredCodes = {};
          let hasStockData = false;

          // Filter codes to only include those with at least one site having stock data
          for (const codeId of Object.keys(userCodes)) {
            const sites = userCodes[codeId].sites || {};
            let codeHasStock = false;

            for (const siteId of Object.keys(sites)) {
              const site = sites[siteId];
              if (
                (site.currentStock != null && site.currentStock !== "" && !isNaN(site.currentStock)) ||
                (site.stockCapacity != null && site.stockCapacity !== "" && !isNaN(site.stockCapacity)) ||
                site.stockPercentage != null ||
                site.stockStatus
              ) {
                codeHasStock = true;
                hasStockData = true;
                break;
              }
            }

            if (codeHasStock) {
              filteredCodes[codeId] = userCodes[codeId];
            }
          }

          if (hasStockData) {
            const profileRef = ref(db, `profileDashboard/${userId}`);
            const profilePromise = new Promise((resolve) => {
              onValue(profileRef, (profileSnap) => {
                const profileData = profileSnap.val() || {};
                const companyName = profileData.company_name || "Unknown Company";
                const phone = profileData.phone || "Unknown Phone";
                const username = profileData.username || "Unknown Name";

                processedUsers[userId] = {
                  companyName,
                  phone,
                  username,
                  codes: filteredCodes,
                };
                resolve();
              }, { onlyOnce: true }); // Fetch profile once, but you can change to real-time if needed
            });

            fetchPromises.push(profilePromise);
          }
        }

        Promise.all(fetchPromises).then(() => {
          const sortedUsers = Object.keys(processedUsers)
            .sort((a, b) =>
              processedUsers[a].companyName.localeCompare(processedUsers[b].companyName)
            )
            .reduce((obj, key) => {
              obj[key] = processedUsers[key];
              return obj;
            }, {});

          setUsers(sortedUsers);

          const firstUserId = Object.keys(sortedUsers)[0];
          if (firstUserId) {
            setSelected({ userId: firstUserId });
            setCodes(sortedUsers[firstUserId].codes);
          }

          setLoading(false);
        });
      } else {
        console.warn("No data in new-code.");
        setLoading(false);
      }
    }, (error) => {
      console.error("Error fetching users and codes:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [adminId]);

  const handleUserSelect = (userId) => {
    setSelected({ userId });
    setCodes(users[userId].codes);
    setIsUserDropdownOpen(false);
  };

  const getStockFillClass = (percentage) => {
    if (!percentage) return "";
    const perc = parseFloat(percentage);
    if (perc > 60) return "green";
    if (perc >= 30) return "yellow";
    return "red";
  };

  const getStockMessage = (percentage) => {
    if (!percentage)
      return { icon: <FaBatteryEmpty />, message: "Stock level: Not set" };
    const perc = parseFloat(percentage);
    if (perc > 60) {
      return { icon: <FaBatteryFull />, message: `Stock level: ${perc}% (High)` };
    }
    if (perc >= 30) {
      return { icon: <FaBatteryHalf />, message: `Stock level: ${perc}% (Medium)` };
    }
    return {
      icon: <FaBatteryEmpty />,
      message: `Stock level: ${perc}% (Low)`,
    };
  };

  const stockAlert = () => {
    alert(
      "This page shows only users who have updated their stock in the DB.\n\n" +
        "1. Select a user from the dropdown (all stock-updated companies appear).\n" +
        "2. View their sites with stock details and animated fill levels."
    );
  };

  // IntersectionObserver for scroll animation
  useEffect(() => {
    const fills = document.querySelectorAll(".admin-cylinder-fill");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target;
            target.style.height = target.dataset.percentage;
            target.style.transition = "height 1.5s ease-in-out";
          }
        });
      },
      { threshold: 0.1 }
    );

    fills.forEach((fill) => {
      fill.style.height = "0%";
      observer.observe(fill);
    });

    return () => observer.disconnect();
  }, [codes]);

  return (
    <div className="admin-stock-container">
      <div className="admin-stock-card">
        <h2 className="admin-stock-title">
          Stock Status
          <FaInfoCircle
            style={{
              cursor: "pointer",
              fontSize: "1.2rem",
              color: "#3b82f6",
            }}
            onClick={stockAlert}
          />
        </h2>

        {loading ? (
          <div className="admin-loading-container">
            <div className="admin-loading-spinner"></div>
          </div>
        ) : Object.keys(users).length === 0 ? (
          <div className="admin-empty-state">
            <p className="admin-empty-text">No users have updated stock yet.</p>
          </div>
        ) : (
          <div className="admin-dashboard-content">
            {/* User Dropdown */}
            <div className="admin-select-container">
              <label className="admin-select-label">
                Select User
              </label>
              <div className="admin-custom-dropdown">
                <div
                  className="admin-dropdown-toggle"
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                >
                  <span>
                    {selected.userId
                      ? users[selected.userId].companyName
                      : "Select a user"}
                  </span>
                  <FaChevronDown
                    style={{
                      marginLeft: "8px",
                      fontSize: "0.9rem",
                      color: "#555",
                      transition: "transform 0.3s ease",
                      transform: isUserDropdownOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    }}
                  />
                </div>
                {isUserDropdownOpen && (
                  <ul className="admin-dropdown-menu">
                    {Object.keys(users).map((userId) => (
                      <li
                        key={userId}
                        className="admin-dropdown-item"
                        onClick={() => handleUserSelect(userId)}
                      >
                        {users[userId].companyName} 
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* User Details and Sites List */}
            {selected.userId && (
              <div className="admin-user-details">
                <h3 className="admin-user-title">
                  {users[selected.userId].companyName}
                </h3>
                <p className="admin-user-phone">
                  Phone Number: {users[selected.userId].phone}
                </p>
                <p className="admin-user-name">
                  Name: {users[selected.userId].username}
                </p>
                <h4 className="admin-sites-title">Sites with Updated Stock</h4>

                {Object.keys(codes).length === 0 ? (
                  <p className="admin-empty-text">No sites found.</p>
                ) : (
                  <>
                    {Object.entries(codes).flatMap(([codeId, code]) =>
                      Object.entries(code.sites || {}).map(([siteId, site]) => {
                        const hasStock =
                          (site.currentStock != null &&
                            site.currentStock !== "" &&
                            !isNaN(site.currentStock)) ||
                          (site.stockCapacity != null &&
                            site.stockCapacity !== "" &&
                            !isNaN(site.stockCapacity)) ||
                          site.stockPercentage ||
                          site.stockStatus;

                        if (!hasStock) return null;

                        return (
                          <div
                            key={`${codeId}-${siteId}`}
                            className="admin-site-card"
                          >
                            <div className="admin-site-details">
                              <p>
                                <span className="admin-detail-label">
                                  Code ID:
                                </span>{" "}
                                {code.codeID || "N/A"}
                              </p>
                              <p>
                                <span className="admin-detail-label">
                                  Brand:
                                </span>{" "}
                                {code.brand || "N/A"}
                              </p>
                              <p>
                                <span className="admin-detail-label">
                                  Address:
                                </span>{" "}
                                {site.address}
                              </p>
                              {site.pincode && (
                               <p>
                              <span className="admin-detail-label">Pincode:</span> {site.pincode}
                                </p>
                              )}
                              <p>
                                <span className="admin-detail-label">
                                  State:
                                </span>{" "}
                                {site.state}
                              </p>
                              <p>
                                <span className="admin-detail-label">
                                  Capacity:
                                </span>{" "}
                                {site.stockCapacity || "N/A"}
                              </p>
                              <p>
                                <span className="admin-detail-label">
                                  Current Stock:
                                </span>{" "}
                                {site.currentStock || "N/A"}
                              </p>
                               <p>
                                <span className="admin-detail-label">
                                  Stock Type:
                                </span>{" "}
                                {site.stockUnit || "N/A"}
                              </p>
                            </div>

                            {/* Animated Container Fill */}
                            <div className="admin-stock-visual-container">
                              <h4 className="admin-stock-label">
                                Stock Level Visualization
                              </h4>
                              <div className="admin-stock-row">
                                <div className="admin-cylinder-container">
                                  <div className="admin-cylindrical">
                                    <div
                                      className={`admin-cylinder-fill ${getStockFillClass(
                                        site.stockPercentage
                                      )}`}
                                      style={{ height: "0%" }}
                                      data-percentage={
                                        site.stockPercentage
                                          ? `${site.stockPercentage}%`
                                          : "0%"
                                      }
                                    ></div>
                                    <div className="threshold-lines">
                                      <div className="admin-threshold-low"></div>
                                      <div className="admin-threshold-medium"></div>
                                      <div className="admin-threshold-high"></div>
                                    </div>
                                  </div>
                                </div>
                                <div className="admin-stock-message">
                                  <span className="admin-message-icon">
                                    {getStockMessage(site.stockPercentage).icon}
                                  </span>
                                  <p className="admin-status-text">
                                    {
                                      getStockMessage(site.stockPercentage)
                                        .message
                                    }
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminStockStatus;